// UI Configuration System Types
//
// MVP Note (PE-737): Permission helpers (canModifyUIConfig, canViewUIConfig) now
// check shouldBypassRoleChecks() first. When true, all users have full access.
// See: /docs/mvp/RBAC_SYSTEM_DOCUMENTATION.md

import { shouldBypassRoleChecks } from '../config/mvpConfig';

export type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'radio'
  | 'file'
  | 'image'
  | 'url'
  | 'lookup'
  | 'formula'
  | 'auto-number';

export type FieldValidation = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  customMessage?: string;
};

export type SelectOption = {
  value: string;
  label: string;
  color?: string;
  description?: string;
  isDefault?: boolean;
  isActive?: boolean;
};

export interface FieldConfig {
  id: string;
  name: string;
  label: string;
  type: FieldType;
  description?: string;
  helpText?: string;
  placeholder?: string;
  defaultValue?: any;
  isRequired: boolean;
  isReadOnly: boolean;
  isVisible: boolean;
  isCustom: boolean;
  applicationId: string; // 'regal', 'credit_management', 'cybersweep', 'credit_block', 'global'
  objectType: string; // 'client', 'audit', 'case', 'task', 'user', 'paw', etc.
  validation?: FieldValidation;
  options?: SelectOption[]; // For select/multiselect/radio
  lookupConfig?: {
    targetObject: string;
    displayField: string;
    valueField: string;
    filterFields?: string[];
  };
  formulaConfig?: {
    expression: string;
    dependentFields: string[];
  };
  conditionalVisibility?: {
    field: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
    value: any;
  };
  displayOrder: number;
  columnWidth?: number; // 1-12 for grid layout
  section: string;
  isShared?: boolean; // Can this field be used across applications
  sharedWith?: string[]; // List of applications this field is shared with
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface SectionConfig {
  id: string;
  name: string;
  label: string;
  description?: string;
  isCollapsible: boolean;
  isCollapsedByDefault: boolean;
  isVisible: boolean;
  isCustom: boolean;
  applicationId: string; // 'regal', 'credit_management', 'cybersweep', 'credit_block', 'global'
  objectType: string; // 'client', 'audit', 'case', 'task', 'user', 'paw', etc.
  displayOrder: number;
  columns: number; // 1, 2, or 3 column layout
  conditionalVisibility?: {
    field: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
    value: any;
  };
  isShared?: boolean;
  sharedWith?: string[];
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface TabConfig {
  id: string;
  name: string;
  label: string;
  icon?: string;
  description?: string;
  isVisible: boolean;
  isCustom: boolean;
  applicationId: string; // 'regal', 'credit_management', 'cybersweep', 'credit_block', 'global'
  objectType: string; // 'client', 'audit', 'case', 'task', 'user', 'paw', etc.
  displayOrder: number;
  permissions?: {
    view: string[];
    edit: string[];
  };
  sections: string[]; // Section IDs in display order
  isShared?: boolean;
  sharedWith?: string[];
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface PageConfig {
  id: string;
  name: string;
  label: string;
  objectType: string; // client, task, user, etc.
  pageType: 'detail' | 'list' | 'create' | 'edit' | 'dashboard';
  description?: string;
  isActive: boolean;
  isCustom: boolean;
  layout: 'single-column' | 'two-column' | 'three-column' | 'tabs';
  tabs?: string[]; // Tab IDs if layout is 'tabs'
  sections?: string[]; // Section IDs if layout is not 'tabs'
  listViewConfig?: {
    fields: string[];
    sortField: string;
    sortDirection: 'asc' | 'desc';
    pageSize: number;
    filters: FieldConfig[];
    actions: ActionConfig[];
  };
  permissions: {
    view: string[];
    create: string[];
    edit: string[];
    delete: string[];
  };
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface ActionConfig {
  id: string;
  name: string;
  label: string;
  type: 'button' | 'link' | 'dropdown';
  icon?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  action: 'navigate' | 'api_call' | 'javascript' | 'workflow';
  actionConfig: {
    url?: string;
    endpoint?: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    script?: string;
    workflowId?: string;
    confirmMessage?: string;
  };
  conditionalVisibility?: {
    field: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
    value: any;
  };
  permissions: string[];
  displayOrder: number;
  isCustom: boolean;
  createdBy: string;
  createdDate: string;
}

export interface AppConfig {
  id: string;
  name: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  isActive: boolean;
  isCustom: boolean;
  version: string;
  objects: ObjectConfig[];
  navigation: NavigationConfig[];
  dashboards: DashboardConfig[];
  workflows: WorkflowConfig[];
  permissions: {
    access: string[];
    admin: string[];
  };
  integrations?: {
    salesforce?: boolean;
    aws?: boolean;
    dynamodb?: boolean;
    s3?: boolean;
  };
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface ObjectConfig {
  id: string;
  name: string;
  label: string;
  labelPlural: string;
  description?: string;
  isCustom: boolean;
  fields: string[]; // Field IDs
  recordTypes?: RecordTypeConfig[];
  triggers?: TriggerConfig[];
  validationRules?: ValidationRuleConfig[];
  permissions: {
    view: string[];
    create: string[];
    edit: string[];
    delete: string[];
  };
  createdBy: string;
  createdDate: string;
}

export interface RecordTypeConfig {
  id: string;
  name: string;
  label: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  availableFields: string[];
  requiredFields: string[];
  readOnlyFields: string[];
  hiddenFields: string[];
  permissions: string[];
}

export interface NavigationConfig {
  id: string;
  name: string;
  label: string;
  type: 'page' | 'app' | 'url' | 'separator';
  target?: string;
  icon?: string;
  parentId?: string;
  displayOrder: number;
  permissions: string[];
  isVisible: boolean;
  isCustom: boolean;
}

export interface DashboardConfig {
  id: string;
  name: string;
  label: string;
  description?: string;
  isDefault: boolean;
  layout: 'grid' | 'flex';
  components: DashboardComponentConfig[];
  permissions: {
    view: string[];
    edit: string[];
  };
  refreshInterval?: number; // in seconds
  isCustom: boolean;
  createdBy: string;
  createdDate: string;
}

export interface DashboardComponentConfig {
  id: string;
  type: 'chart' | 'list' | 'metric' | 'calendar' | 'custom';
  title: string;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  dataSource: {
    object: string;
    fields: string[];
    filters?: any[];
    groupBy?: string;
    aggregation?: 'count' | 'sum' | 'avg' | 'min' | 'max';
  };
  chartConfig?: {
    type: 'line' | 'bar' | 'pie' | 'donut' | 'area';
    xAxis: string;
    yAxis: string;
    colors?: string[];
  };
  listConfig?: {
    fields: string[];
    maxRows: number;
    showActions: boolean;
  };
  refreshInterval?: number;
}

export interface WorkflowConfig {
  id: string;
  name: string;
  label: string;
  description?: string;
  trigger: 'record_created' | 'record_updated' | 'record_deleted' | 'field_changed' | 'manual' | 'scheduled';
  triggerConditions?: any[];
  actions: WorkflowActionConfig[];
  isActive: boolean;
  isCustom: boolean;
  createdBy: string;
  createdDate: string;
}

export interface WorkflowActionConfig {
  id: string;
  type: 'field_update' | 'email_alert' | 'task_creation' | 'api_call' | 'notification';
  order: number;
  config: any;
  conditions?: any[];
}

export interface TriggerConfig {
  id: string;
  name: string;
  event: 'before_insert' | 'after_insert' | 'before_update' | 'after_update' | 'before_delete' | 'after_delete';
  script: string;
  isActive: boolean;
  isCustom: boolean;
}

export interface ValidationRuleConfig {
  id: string;
  name: string;
  description?: string;
  errorMessage: string;
  formula: string;
  isActive: boolean;
  isCustom: boolean;
}

export interface UIConfigChangeLog {
  id: string;
  changeType: 'field_added' | 'field_modified' | 'field_deleted' | 'section_added' | 'section_modified' | 'section_deleted' | 'tab_added' | 'tab_modified' | 'tab_deleted' | 'page_modified' | 'app_modified';
  objectType: string;
  objectId: string;
  oldValue?: any;
  newValue?: any;
  changedBy: string;
  changeDate: string;
  reason?: string;
  impact: 'low' | 'medium' | 'high';
}

// Helper functions for UI configuration
export const canModifyUIConfig = (userRole: string): boolean => {
  // MVP Mode (PE-737): All users can modify UI config
  if (shouldBypassRoleChecks()) {
    return true;
  }
  return userRole === 'system_admin';
};

export const canViewUIConfig = (userRole: string): boolean => {
  // MVP Mode (PE-737): All users can view UI config
  if (shouldBypassRoleChecks()) {
    return true;
  }
  return userRole === 'system_admin' || userRole === 'product_manager';
};

export const validateFieldConfig = (field: FieldConfig): string[] => {
  const errors: string[] = [];

  if (!field.name || !field.name.match(/^[a-zA-Z][a-zA-Z0-9_]*$/)) {
    errors.push('Field name must start with a letter and contain only letters, numbers, and underscores');
  }

  if (!field.label || field.label.trim().length === 0) {
    errors.push('Field label is required');
  }

  if (field.type === 'select' || field.type === 'multiselect' || field.type === 'radio') {
    if (!field.options || field.options.length === 0) {
      errors.push('Options are required for select, multiselect, and radio fields');
    }
  }

  if (field.type === 'lookup' && !field.lookupConfig) {
    errors.push('Lookup configuration is required for lookup fields');
  }

  if (field.type === 'formula' && !field.formulaConfig) {
    errors.push('Formula configuration is required for formula fields');
  }

  return errors;
};

export const getFieldTypeOptions = (): SelectOption[] => [
  { value: 'text', label: 'Text', description: 'Single line text input' },
  { value: 'email', label: 'Email', description: 'Email address with validation' },
  { value: 'phone', label: 'Phone', description: 'Phone number with formatting' },
  { value: 'number', label: 'Number', description: 'Numeric input with validation' },
  { value: 'currency', label: 'Currency', description: 'Currency amount with formatting' },
  { value: 'date', label: 'Date', description: 'Date picker' },
  { value: 'datetime', label: 'Date/Time', description: 'Date and time picker' },
  { value: 'textarea', label: 'Text Area', description: 'Multi-line text input' },
  { value: 'select', label: 'Picklist', description: 'Single selection from predefined options' },
  { value: 'multiselect', label: 'Multi-Select Picklist', description: 'Multiple selections from predefined options' },
  { value: 'checkbox', label: 'Checkbox', description: 'True/false checkbox' },
  { value: 'radio', label: 'Radio Buttons', description: 'Single selection from radio buttons' },
  { value: 'file', label: 'File Upload', description: 'File attachment' },
  { value: 'image', label: 'Image Upload', description: 'Image file with preview' },
  { value: 'url', label: 'URL', description: 'Web address with validation' },
  { value: 'lookup', label: 'Lookup', description: 'Reference to another record' },
  { value: 'formula', label: 'Formula', description: 'Calculated field based on other fields' },
  { value: 'auto-number', label: 'Auto Number', description: 'Automatically generated sequential number' }
];